import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { color, studyStatusTheme, type StudyStatus } from '../styles/theme';
import {
  useUpdateStudy,
  type Study,
  type StudyCta,
  type StudyInput,
  type StudyQuestion,
  type QuestionType,
} from '../api/studies';
import {
  Card,
  CardTitle,
  Field,
  Button,
  RoundButton,
  Meter,
  inputStyle,
  selectStyle,
} from '../components/ui';
import { IconArrowLeft, IconPlus, IconUpload, IconLock, IconMail } from '../components/icons';
import { DeleteStudy } from './DeleteStudy';
import { SITE_URL } from '../lib/site';
import { ErrorNote } from '../components/ui';

const textareaStyle = { ...inputStyle, resize: 'vertical' as const, lineHeight: '22px' };

/**
 * For a role that can only look, a disabled fieldset switches off every
 * control inside it at once. It wraps the parts that edit the study rather
 * than the whole page, so "Preview as participant" — which changes nothing —
 * stays usable for that role.
 */
const lockStyle: CSSProperties = { border: 0, padding: 0, margin: 0, minWidth: 0 };

/**
 * The study page's primary button, and where the public site honours each
 * choice (its `ctaFits`): an intake call only while Actively Recruiting, the
 * waitlist only while In Development, updates and "Learn more only" at any
 * status. A choice its status cannot carry is not shown — the page offers
 * what the status alone offers instead (`STATUS_CTA`).
 */
const CTA_CHOICES: ReadonlyArray<{ key: StudyCta; label: string; only?: StudyStatus }> = [
  { key: 'intake', label: 'Request an intake call', only: 'recruiting' },
  { key: 'waitlist', label: 'Join the waitlist', only: 'development' },
  { key: 'subscribe', label: 'Subscribe for updates' },
  { key: 'none', label: 'Learn more only' },
];

/** What the site shows for a status when the chosen button does not fit it (its `ctaOf`). */
const STATUS_CTA: Record<StudyStatus, StudyCta> = {
  recruiting: 'intake',
  development: 'waitlist',
  active: 'none',
  completed: 'none',
  invitation: 'none',
};

const ctaFits = (cta: StudyCta, status: StudyStatus): boolean => {
  const only = CTA_CHOICES.find((c) => c.key === cta)?.only;
  return !only || only === status;
};

const ctaLabel = (cta: StudyCta): string => CTA_CHOICES.find((c) => c.key === cta)?.label ?? cta;

/**
 * The trial phase, picked rather than typed (21 Oct 2026). The study row keeps
 * one `phase` string, as the API and the website read it: the phase, then the
 * design words, joined by " · " ("Phase 1b · Open-label"). The website draws
 * its phase track from the phase and shows each design part as a tag, so the
 * editor composes the string and nobody has to remember the separator.
 */
const PHASES = ['Early Phase 1', 'Phase 1', 'Phase 1b', 'Phase 1/2', 'Phase 2', 'Phase 2/3', 'Phase 3', 'Phase 4'] as const;

/** A stored phase line as its phase and its design. Reads lines typed before
 *  the dropdown too: "Phase 1b, Open-Label" is Phase 1b and "Open-Label". */
function splitPhase(value: string): { stage: string; design: string } {
  const m = /^\s*((?:early\s+)?phase\s*(?:1\/2|2\/3|1b|[1-4]))(?![\w/])\s*[·,;:–-]?\s*(.*)$/i.exec(value);
  const typed = (m?.[1] ?? '').toLowerCase().replace(/\s+/g, ' ');
  const stage = PHASES.find((p) => p.toLowerCase() === typed);
  if (!m || !stage) return { stage: '', design: value.trimStart() };
  return { stage, design: m[2] ?? '' };
}

/** The design is kept as typed, so a "·" at its end survives until the next word. */
const joinPhase = (stage: string, design: string): string =>
  stage && design.trim() ? `${stage} · ${design}` : stage || design;

/** Unique per question; the API caps an id at 60 characters, a UUID is 36. */
function newQuestionId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `q-${uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`}`;
}

/**
 * The fields this form owns. `slug`, `statusLabel`, `sortOrder` and
 * `boardEnrolled` are the server's, and `mailbox` is Microsoft 365's (a body
 * naming it is refused), so they are deliberately absent — nothing here may
 * send them.
 */
const EDITABLE = [
  'code',
  'label',
  'publicTitle',
  'short',
  'status',
  'published',
  'description',
  'eligibility',
  'sponsor',
  'site',
  'pi',
  'phase',
  'funding',
  'tags',
  'nctId',
  'registryTitle',
  'image',
  'cta',
  'target',
  'enrolled',
  'connectedSlug',
  'connectedLabel',
  'questions',
] as const satisfies readonly (keyof StudyInput)[];

/**
 * The nine fields every participant answers, whichever study they pick. Age,
 * not date of birth, since September 2026 (the site's intake-page.tsx).
 */
const GENERAL_FIELDS = [
  ['First name *', 'Jane'],
  ['Last name *', 'Doe'],
  ['Pronoun preference', 'Select an option'],
  ['Age *', '42'],
  ['Phone *', '(203) 555-0100'],
  ['Email *', 'you@example.com'],
  ['City *', 'New Haven'],
  ['State *', 'CT'],
  ['How did you hear about ENACT?', 'Select an option'],
] as const;

const CONDITIONS = ['Depression', 'PTSD', 'Anxiety', 'OCD', 'Substance use', 'Other'];

const QUESTION_TYPES: Array<[QuestionType, string]> = [
  ['yesno', 'Yes / No'],
  ['choice', 'Multiple choice'],
  ['checks', 'Checkboxes'],
  ['short', 'Short text'],
  ['long', 'Long text'],
  ['number', 'Number'],
];

function optionsOf(q: StudyQuestion): string[] {
  return q.options
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** How one screening question renders on the public form. */
function QuestionPreview({ q }: { q: StudyQuestion }) {
  const small = { ...inputStyle, borderRadius: 10, padding: '9px 12px', fontSize: 13.5 };

  if (q.type === 'yesno') {
    return (
      <span style={{ display: 'flex', gap: 18 }}>
        {['Yes', 'No'].map((o) => (
          <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5 }}>
            <input type="radio" disabled style={{ accentColor: color.blue }} />
            {o}
          </label>
        ))}
      </span>
    );
  }
  if (q.type === 'choice' || q.type === 'checks') {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {optionsOf(q).map((o) => (
          <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5 }}>
            <input
              type={q.type === 'choice' ? 'radio' : 'checkbox'}
              disabled
              style={{ accentColor: color.blue }}
            />
            {o}
          </label>
        ))}
        {optionsOf(q).length === 0 && (
          <span style={{ fontSize: 12.5, color: color.muted }}>No options set yet.</span>
        )}
      </span>
    );
  }
  if (q.type === 'long') {
    return <textarea rows={2} disabled placeholder="Your answer" style={{ ...small, resize: 'vertical' }} />;
  }
  if (q.type === 'number') {
    return <input type="number" disabled placeholder="0" style={{ ...small, maxWidth: 120 }} />;
  }
  return <input disabled placeholder="Your answer" style={{ ...small, maxWidth: 340 }} />;
}

interface Props {
  study: Study;
  others: Study[];
  canManage: boolean;
  showPreview: boolean;
  onTogglePreview: () => void;
  onBack: () => void;
}

export function StudyEditor({
  study,
  others,
  canManage,
  showPreview,
  onTogglePreview,
  onBack,
}: Props) {
  const update = useUpdateStudy();
  const [confirmDelete, setConfirmDelete] = useState(false);

  /**
   * The form edits a local draft and saves on demand.
   *
   * The alternative — writing each field through as it changes — meant a
   * request per keystroke group and no way to abandon a half-finished edit.
   * Holding a draft also makes "what have I changed" answerable, which is what
   * the Save button needs to know.
   */
  const [draft, setDraft] = useState<Study>(study);

  // Re-seed only when a different study is opened. A background refetch must
  // never overwrite what someone is halfway through typing; the server's own
  // copy is folded in by `save()` instead, from the response it returns.
  useEffect(() => setDraft(study), [study.slug]);

  /** Only what actually differs from the saved copy — the API patches per field. */
  const changes = useMemo(() => {
    const out: Partial<StudyInput> = {};
    for (const key of EDITABLE) {
      const a = draft[key];
      const b = study[key];
      const same =
        key === 'questions'
          ? JSON.stringify(a) === JSON.stringify(b)
          : a === b;
      if (!same) (out as Record<string, unknown>)[key] = a;
    }
    return out;
  }, [draft, study]);

  const dirty = Object.keys(changes).length > 0;
  const [saved, setSaved] = useState(false);

  async function save(extra: Partial<StudyInput> = {}): Promise<void> {
    const patch: Partial<StudyInput> = { ...changes, ...extra };
    if (Object.keys(patch).length === 0) return;
    // The short name — what the sidebar and boards show — has no field here.
    // While it is still the one the API made from the title (a new study's is
    // "Untitled study"), it follows a retitle instead of keeping the old words.
    if (patch.label !== undefined && study.short === study.label.slice(0, 80)) {
      patch.short = patch.label.trim().slice(0, 80);
    }
    setSaved(false);
    try {
      // Adopt the saved row: the server derives `short` from the title when it
      // is blank, so the form should show what was actually stored.
      const res = await update.mutateAsync({ slug: study.slug, patch });
      setDraft(res.study);
      setSaved(true);
    } catch {
      // Surfaced through `update.error`; the draft keeps the edits so they can
      // be corrected and retried rather than retyped.
    }
  }

  // A save confirmation is only true until the next edit.
  useEffect(() => {
    if (dirty) setSaved(false);
  }, [dirty]);

  function goBack(): void {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    onBack();
  }

  const edit = (p: Partial<Study>) => setDraft((d) => ({ ...d, ...p }));
  const phaseParts = splitPhase(draft.phase ?? '');

  const set =
    <K extends keyof Study>(k: K) =>
    (e: { target: { value: string } }) =>
      edit({ [k]: e.target.value } as never);

  const setInt =
    (k: 'target' | 'enrolled') =>
    (e: { target: { value: string } }) =>
      edit({ [k]: Math.max(0, parseInt(e.target.value, 10) || 0) });

  /**
   * Question edits go through the functional updater, not the closed-over
   * draft: two clicks of "Add question" inside one render would otherwise both
   * build from the same starting array and the first would be lost.
   */
  const editQuestions = (fn: (qs: StudyQuestion[]) => StudyQuestion[]) =>
    setDraft((d) => ({ ...d, questions: fn(d.questions) }));

  const addQuestion = () =>
    editQuestions((qs) => [
      ...qs,
      // `Date.now()` alone collided when two were added in the same
      // millisecond, which made one row edit and delete the other.
      { id: newQuestionId(), label: '', type: 'yesno', required: false, options: '' },
    ]);

  const updateQuestion = (id: string, p: Partial<StudyQuestion>) =>
    editQuestions((qs) => qs.map((q) => (q.id === id ? { ...q, ...p } : q)));

  const removeQuestion = (id: string) =>
    editQuestions((qs) => qs.filter((q) => q.id !== id));

  const moveQuestion = (index: number, dir: -1 | 1) =>
    editQuestions((qs) => {
      const to = index + dir;
      if (to < 0 || to >= qs.length) return qs;
      const next = qs.slice();
      [next[index], next[to]] = [next[to]!, next[index]!];
      return next;
    });

  const pct = draft.target > 0 ? Math.min(100, Math.round((draft.enrolled / draft.target) * 100)) : 0;
  const published = study.published;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={goBack}
          className="hov-tint"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            border: 'none',
            color: color.blue,
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
            padding: '8px 10px',
            borderRadius: 10,
          }}
        >
          <IconArrowLeft /> All studies
        </button>
        <span style={{ flex: 1 }} />

        {/* Where this study is, and — when it is live — how to go and look at
            it. The research page groups by status rather than giving each study
            its own URL, so the link lands on the right section. */}
        {published ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#14684c' }}>
              ● Published — live on the site
            </span>
            <a
              href={`${SITE_URL}/research#section-${draft.status}`}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12.5, fontWeight: 700, color: color.blue }}
            >
              View on the site ↗
            </a>
          </span>
        ) : (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>
            ○ Draft — not visible on the site
          </span>
        )}

        {canManage && (
          <>
            {/* Status of the draft, so "did that save?" never needs guessing. */}
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: dirty ? '#8a4f16' : saved ? color.green : color.muted,
                minWidth: 96,
                textAlign: 'right',
              }}
            >
              {update.isPending
                ? 'Saving…'
                : dirty
                  ? 'Unsaved changes'
                  : saved
                    ? 'Saved'
                    : ''}
            </span>

            {/* Destructive, so it sits away from Save and carries its own
                typed confirmation rather than a window.confirm. */}
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              disabled={update.isPending}
              title="Delete this study"
              style={{ color: '#a4432f', borderColor: '#f0cdc4' }}
            >
              Delete
            </Button>

            <Button onClick={() => void save()} disabled={!dirty || update.isPending}>
              Save changes
            </Button>

            {/* Publishing commits whatever is on screen — staging a publish
                behind a second button would be a trap. */}
            <Button
              variant="navy"
              onClick={() => void save({ published: !published })}
              disabled={update.isPending}
            >
              {published ? 'Revert to draft' : dirty ? 'Save & publish' : 'Publish study'}
            </Button>
          </>
        )}
      </div>

      {update.error && <ErrorNote message={(update.error as Error).message} />}

      {!canManage && (
        <p
          style={{
            background: color.tint,
            border: `1px solid ${color.tint2}`,
            borderRadius: 14,
            padding: '12px 16px',
            fontSize: 12.5,
            color: color.blueDeep,
            margin: 0,
          }}
        >
          Your role can view studies but not change them.
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', minWidth: 0 }}>
        {/* ── Left column: the study record and its screening questions ── */}
        <div style={{ flex: '2 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <fieldset disabled={!canManage} style={lockStyle}>
            <Card pad={24} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <CardTitle>Study details</CardTitle>

              <Field label="Title">
                <input
                  value={draft.label}
                  onChange={set('label')}
                  placeholder="e.g. Open-Label Psilocybin Study…"
                  style={{ ...inputStyle, fontSize: 15, fontWeight: 700, padding: '11px 14px' }}
                />
              </Field>

              <Field label="Public title" hint="— in plain words, above the title on the website; blank shows the title alone">
                <input
                  value={draft.publicTitle}
                  onChange={set('publicTitle')}
                  placeholder="e.g. Psilocybin and Brain Changes in Opioid Use Disorder"
                  style={inputStyle}
                />
              </Field>

              <Field label="Description">
                <textarea
                  value={draft.description}
                  onChange={set('description')}
                  rows={6}
                  placeholder="What the study investigates and why it matters."
                  style={textareaStyle}
                />
              </Field>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
                  gap: 12,
                }}
              >
                <Field label="Sponsor">
                  <input value={draft.sponsor} onChange={set('sponsor')} placeholder="e.g. Yale University" style={inputStyle} />
                </Field>
                <Field label="Site">
                  <input value={draft.site} onChange={set('site')} placeholder="e.g. Connecticut Mental Health Center, New Haven" style={inputStyle} />
                </Field>
                <Field label="Principal investigator">
                  <input value={draft.pi} onChange={set('pi')} placeholder="e.g. Benjamin Kelmendi, M.D." style={inputStyle} />
                </Field>
                <Field label="Phase">
                  <select
                    value={phaseParts.stage}
                    onChange={(e) => edit({ phase: joinPhase(e.target.value, phaseParts.design) })}
                    style={selectStyle}
                  >
                    <option value="">No phase (not applicable)</option>
                    {PHASES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Design" hint="— each part, split with ·, is a tag on the site">
                  <input
                    value={phaseParts.design}
                    onChange={(e) => edit({ phase: joinPhase(phaseParts.stage, e.target.value) })}
                    placeholder="e.g. Open-label · Randomized"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Funded by" hint="— internal, not on the site">
                  <input value={draft.funding} onChange={set('funding')} placeholder="e.g. CT DMHAS" style={inputStyle} />
                </Field>
              </div>

              <Field
                label="Condition tags"
                hint="— comma-separated, shown on the study card"
              >
                <input
                  value={draft.tags}
                  onChange={set('tags')}
                  placeholder="e.g. PTSD, Depression, Anxiety"
                  style={inputStyle}
                />
              </Field>

              <Field
                label="Who may be eligible"
                hint="— a heading line, “- ” bullets under it, a blank line between groups"
              >
                <textarea
                  value={draft.eligibility}
                  onChange={set('eligibility')}
                  rows={10}
                  placeholder={'Who may be eligible\n- Adults 18 and older\n\nWhat would rule someone out\n- …'}
                  style={textareaStyle}
                />
              </Field>

              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <Field label="Study code" style={{ maxWidth: 200 }}>
                  <input
                    value={draft.code}
                    onChange={set('code')}
                    placeholder="e.g. SOA-P2"
                    style={{ ...inputStyle, textTransform: 'uppercase' }}
                  />
                </Field>
                <Field label="ClinicalTrials.gov ID" style={{ maxWidth: 260 }}>
                  <input value={draft.nctId} onChange={set('nctId')} placeholder="e.g. NCT06442423" style={inputStyle} />
                </Field>
              </div>

              <Field label="Registry title" hint="— the ClinicalTrials.gov title, shown under the link">
                <input
                  value={draft.registryTitle}
                  onChange={set('registryTitle')}
                  placeholder="e.g. Safety, Feasibility, and Tolerability of Psilocybin…"
                  style={inputStyle}
                />
              </Field>
            </Card>
          </fieldset>

          <Card pad={24} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <CardTitle style={{ flex: 1 }}>
                Screening questions{' '}
                <span style={{ fontSize: 13, color: color.muted, fontWeight: 500 }}>
                  ({draft.questions.length})
                </span>
              </CardTitle>
              {/* Outside every lock: previewing changes nothing, so a role
                  that can only look may still see what a participant would. */}
              <Button variant="ghost" onClick={onTogglePreview} style={{ padding: '7px 15px', fontSize: 12.5 }}>
                {showPreview ? 'Hide preview' : 'Preview as participant'}
              </Button>
              {canManage && (
                <Button onClick={addQuestion} style={{ padding: '8px 16px', fontSize: 12.5 }}>
                  <IconPlus size={13} /> Add question
                </Button>
              )}
            </div>

            {/* The general block is the same on every study, so it is shown but
                not editable — it belongs to the form, not to this record. */}
            <div
              style={{
                border: `1px solid ${color.line}`,
                background: color.bg,
                borderRadius: 16,
                padding: '18px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: color.muted }}>
                <IconLock size={13} />
                <span
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  General questions — asked for every study, not editable
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
                  gap: 12,
                }}
              >
                {GENERAL_FIELDS.map(([label, placeholder]) => (
                  <Field key={label} label={label} style={{ fontSize: 12 }}>
                    <input
                      disabled
                      placeholder={placeholder}
                      style={{ ...inputStyle, borderRadius: 10, padding: '8px 12px', fontSize: 13 }}
                    />
                  </Field>
                ))}
              </div>

              <Field label="Which study would you like to join? *" style={{ maxWidth: 420, fontSize: 12 }}>
                <input
                  readOnly
                  value={draft.label || 'Untitled study'}
                  style={{
                    ...inputStyle,
                    borderRadius: 10,
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                />
              </Field>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: color.muted, margin: 0 }}>
                  Primary condition you are seeking treatment for *
                </p>
                <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {CONDITIONS.map((c) => (
                    <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input type="radio" disabled style={{ accentColor: color.blue }} />
                      {c}
                    </label>
                  ))}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: color.muted, margin: 0 }}>
                  Any other conditions you would like us to know about
                </p>
                <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {CONDITIONS.map((c) => (
                    <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input type="checkbox" disabled style={{ accentColor: color.blue }} />
                      {c}
                    </label>
                  ))}
                </span>
              </div>

              <Field label="Anything else you would like us to know (optional)" style={{ fontSize: 12 }}>
                <textarea
                  disabled
                  rows={2}
                  placeholder="Anything you would like us to know before the call."
                  style={{ ...textareaStyle, borderRadius: 10, padding: '8px 12px', fontSize: 13 }}
                />
              </Field>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, lineHeight: '19px' }}>
                <input type="checkbox" disabled style={{ accentColor: color.blue, marginTop: 2, flex: 'none' }} />
                I understand that this call is for informational purposes only and does not provide
                medical advice or treatment.
              </label>
            </div>

            <p style={{ fontSize: 12.5, fontWeight: 800, color: color.ink, margin: '2px 0 0' }}>
              Study-specific questions{' '}
              <span style={{ fontWeight: 600, color: color.muted }}>
                — appended when this study is selected under “Which study would you like to join?”
              </span>
            </p>

            <fieldset
              disabled={!canManage}
              style={{ ...lockStyle, display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              {draft.questions.map((q, i) => (
                <div
                  key={q.id}
                  style={{
                    background: color.bg,
                    border: `1px solid ${color.line}`,
                    borderRadius: 14,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        display: 'flex',
                        width: 24,
                        height: 24,
                        borderRadius: 8,
                        background: color.tint2,
                        color: color.blueDeep,
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11.5,
                        fontWeight: 800,
                        flex: 'none',
                      }}
                    >
                      {i + 1}
                    </span>
                    <input
                      value={q.label}
                      onChange={(e) => updateQuestion(q.id, { label: e.target.value })}
                      placeholder="Question text"
                      style={{
                        ...inputStyle,
                        flex: 1,
                        minWidth: 0,
                        borderRadius: 10,
                        padding: '9px 12px',
                        fontSize: 13.5,
                        fontWeight: 600,
                      }}
                    />
                    <RoundButton
                      title="Move up"
                      size={26}
                      disabled={i === 0}
                      onClick={() => moveQuestion(i, -1)}
                    >
                      ↑
                    </RoundButton>
                    <RoundButton
                      title="Move down"
                      size={26}
                      disabled={i === draft.questions.length - 1}
                      onClick={() => moveQuestion(i, 1)}
                    >
                      ↓
                    </RoundButton>
                    <RoundButton title="Delete" size={26} onClick={() => removeQuestion(q.id)}>
                      ×
                    </RoundButton>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flexWrap: 'wrap',
                      paddingLeft: 34,
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 12,
                        fontWeight: 700,
                        color: color.muted,
                      }}
                    >
                      Type
                      <select
                        value={q.type}
                        onChange={(e) =>
                          updateQuestion(q.id, { type: e.target.value as QuestionType })
                        }
                        style={{
                          ...selectStyle,
                          width: 'auto',
                          minWidth: 150,
                          borderRadius: 10,
                          padding: '8px 32px 8px 12px',
                          fontSize: 12.5,
                        }}
                      >
                        {QUESTION_TYPES.map(([v, label]) => (
                          <option key={v} value={v}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {(q.type === 'choice' || q.type === 'checks') && (
                      <input
                        value={q.options}
                        onChange={(e) => updateQuestion(q.id, { options: e.target.value })}
                        placeholder="Options, separated by commas"
                        style={{
                          ...inputStyle,
                          flex: 1,
                          minWidth: 160,
                          borderRadius: 10,
                          padding: '8px 12px',
                          fontSize: 12.5,
                        }}
                      />
                    )}

                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: color.muted,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={q.required}
                        onChange={() => updateQuestion(q.id, { required: !q.required })}
                        style={{ width: 14, height: 14, accentColor: color.blue, cursor: 'pointer' }}
                      />
                      Required
                    </label>
                  </div>
                </div>
              ))}

              {draft.questions.length === 0 && (
                <p style={{ fontSize: 13, color: color.muted, margin: 0, padding: '8px 2px' }}>
                  No study-specific questions yet — participants will only complete the general form.
                </p>
              )}
            </fieldset>

            {showPreview && draft.questions.length > 0 && (
              <div
                style={{
                  border: `1px solid ${color.tint2}`,
                  background: color.tint,
                  borderRadius: 16,
                  padding: '20px 22px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                <p
                  style={{
                    fontSize: 11.5,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: color.blueDeep,
                    margin: 0,
                  }}
                >
                  Participant preview — study-specific questions
                </p>
                {draft.questions.map((q) => (
                  <div key={q.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: color.ink, margin: 0 }}>
                      {q.label || 'Untitled question'}
                      {q.required && <span style={{ color: color.red }}> *</span>}
                    </p>
                    <QuestionPreview q={q} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ── Right column: publication settings ── */}
        <fieldset
          disabled={!canManage}
          style={{ ...lockStyle, flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <Card pad={22} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <CardTitle size={16}>Status</CardTitle>
            <select
              value={draft.status}
              onChange={(e) => edit({ status: e.target.value as StudyStatus })}
              style={selectStyle}
            >
              {(Object.keys(studyStatusTheme) as StudyStatus[]).map((k) => (
                <option key={k} value={k}>
                  {studyStatusTheme[k].label}
                </option>
              ))}
            </select>
            <p style={{ fontSize: 12, color: color.muted, margin: 0 }}>
              Sets the badge on the study card and page.
            </p>
          </Card>

          {/* Read only: the mailbox is the lab's Microsoft 365 setup, made with it. */}
          <Card pad={22} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <CardTitle size={16}>Study mailbox</CardTitle>
            {study.mailbox ? (
              <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700, color: color.blueDeep, margin: 0 }}>
                <IconMail size={14} />
                {study.mailbox}
              </p>
            ) : (
              <p style={{ fontSize: 13.5, fontWeight: 600, color: color.muted, margin: 0 }}>No mailbox of its own</p>
            )}
            <p style={{ fontSize: 12, color: color.muted, margin: 0 }}>
              {study.mailbox
                ? 'Staff replies from this study’s records go out from this address, and the person’s answer comes back to it.'
                : 'Staff replies from this study’s records go out from the form’s own mailbox.'}{' '}
              Mailboxes are set up in Microsoft 365, not here.
            </p>
          </Card>

          <Card pad={22} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <CardTitle size={16}>Participants</CardTitle>
            <Field label="Target enrollment" style={{ maxWidth: 130 }}>
              <input type="number" value={draft.target} onChange={setInt('target')} style={inputStyle} />
            </Field>
            {/* Not a Field: a <label> around the − button would send a click on
                the words to it, and take one off the count. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>
                Currently enrolled <span style={{ fontWeight: 600 }}>— kept by hand</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RoundButton
                  size={32}
                  tone="ink"
                  title="One fewer"
                  onClick={() => edit({ enrolled: Math.max(0, draft.enrolled - 1) })}
                >
                  −
                </RoundButton>
                <input
                  type="number"
                  aria-label="Currently enrolled"
                  value={draft.enrolled}
                  onChange={setInt('enrolled')}
                  style={{ ...inputStyle, width: 80, textAlign: 'center' }}
                />
                <RoundButton
                  size={32}
                  tone="ink"
                  title="One more"
                  onClick={() => edit({ enrolled: draft.enrolled + 1 })}
                >
                  +
                </RoundButton>
              </span>
              <span style={{ fontSize: 12, lineHeight: '17px', fontWeight: 500, color: color.muted }}>
                Not counted from the board: marking someone Enrolled there leaves this number as it is, and
                it can include people enrolled before the CRM. Change it here when it should move.
              </span>
            </div>
            {/* The API's own count, carried on the study: no list read, no PHI access. */}
            <p
              style={{
                fontSize: 12,
                lineHeight: '17px',
                color: color.ink,
                background: color.bg,
                border: `1px solid ${color.line}`,
                borderRadius: 10,
                padding: '8px 10px',
                margin: 0,
              }}
            >
              On this study’s board:{' '}
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{study.boardEnrolled}</strong> marked
              Enrolled.
            </p>
            <div>
              <Meter pct={pct} height={8} />
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: color.muted,
                  margin: '7px 0 0',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {draft.enrolled} of {draft.target} enrolled · {pct}%
              </p>
            </div>
            {draft.status === 'recruiting' &&
              !('status' in changes) &&
              draft.target > 0 &&
              draft.enrolled >= draft.target && (
                <p
                  style={{
                    fontSize: 12,
                    lineHeight: '17px',
                    fontWeight: 600,
                    color: color.blueDeep,
                    background: color.tint,
                    borderRadius: 10,
                    padding: '8px 10px',
                    margin: 0,
                  }}
                >
                  Target reached — saving marks enrollment complete, and the site stops offering an intake
                  call.
                </p>
              )}
            <p style={{ fontSize: 12, color: color.muted, margin: 0 }}>
              The target is shown on the study page. The enrolled count is not. When a recruiting study
              reaches its target, it moves to Enrollment Complete — Active on its own.
            </p>
          </Card>

          <Card pad={22} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <CardTitle size={16}>Call to action</CardTitle>
            <select value={draft.cta} onChange={set('cta')} style={selectStyle}>
              {CTA_CHOICES.map((c) => {
                const fits = ctaFits(c.key, draft.status);
                return (
                  // A choice the status cannot carry is offered only while it
                  // is the saved one, so the select still says what is stored.
                  <option key={c.key} value={c.key} disabled={!fits && c.key !== draft.cta}>
                    {c.label}
                    {c.only && !fits ? ` — only while ${studyStatusTheme[c.only].label}` : ''}
                  </option>
                );
              })}
            </select>
            {ctaFits(draft.cta, draft.status) ? (
              <p style={{ fontSize: 12, lineHeight: '17px', color: color.muted, margin: 0 }}>
                The primary button on the study page{draft.cta === 'none' ? ': none, and no form' : ''}. Request
                an intake call shows only while Actively Recruiting, Join the waitlist only while In Development;
                the other two show at any status.
              </p>
            ) : (
              <p
                style={{
                  fontSize: 12,
                  lineHeight: '17px',
                  fontWeight: 600,
                  color: color.blueDeep,
                  background: color.tint,
                  borderRadius: 10,
                  padding: '8px 10px',
                  margin: 0,
                }}
              >
                {ctaLabel(draft.cta)} does not show while the study is {studyStatusTheme[draft.status].label}: the
                page offers {ctaLabel(STATUS_CTA[draft.status])} instead
                {STATUS_CTA[draft.status] === 'none' ? ', with no form' : ''}.
              </p>
            )}
          </Card>

          <Card pad={22} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <CardTitle size={16}>Cover image</CardTitle>
            {draft.image && (
              <img
                src={draft.image}
                alt="Cover"
                style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 12, display: 'block' }}
              />
            )}
            <label
              className="hov-blue"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: color.blue,
                color: color.white,
                fontSize: 12.5,
                fontWeight: 700,
                padding: '10px 0',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              <IconUpload size={14} /> Upload image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const reader = new FileReader();
                  reader.onload = () => edit({ image: String(reader.result) });
                  reader.readAsDataURL(f);
                }}
                style={{ display: 'none' }}
              />
            </label>
            <input
              value={draft.image.startsWith('data:') ? '' : draft.image}
              onChange={set('image')}
              placeholder="…or paste an image URL"
              style={{ ...inputStyle, fontSize: 13 }}
            />
            {draft.image && (
              <button
                onClick={() => edit({ image: '' })}
                className="hov-danger"
                style={{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: 'none',
                  color: color.muted,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: 6,
                }}
              >
                Remove image
              </button>
            )}
          </Card>

          <Card pad={22} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <CardTitle size={16}>Connected study</CardTitle>
            <select value={draft.connectedSlug} onChange={set('connectedSlug')} style={selectStyle}>
              <option value="">None</option>
              {others.map((o) => (
                <option key={o.slug} value={o.slug}>
                  {o.label || 'Untitled study'}
                </option>
              ))}
            </select>
            <input
              value={draft.connectedLabel}
              onChange={set('connectedLabel')}
              placeholder="Relation label, e.g. Parent study"
              style={{ ...inputStyle, fontSize: 13 }}
            />
            <p style={{ fontSize: 12, color: color.muted, margin: 0 }}>
              Shown under “Connected studies” on the study page.
            </p>
          </Card>
        </fieldset>
      </div>

      {confirmDelete && (
        <DeleteStudy
          study={study}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => {
            setConfirmDelete(false);
            onBack();
          }}
        />
      )}
    </div>
  );
}
